import { z } from 'zod';
import { gateSchema, requirementKeySchema } from '../requirements/index.js';
import { requirementExceptionSchema } from '../readiness/vocabulary.js';
export const historicalSourceSchema = z.object({
    system: z.enum(['legacy-dashboard', 'sharepoint', 'external-record']),
    recordId: z.string().trim().min(1).max(500),
    version: z.string().trim().max(200).nullable().default(null),
    recordedBy: z.string().trim().max(200).nullable().default(null),
    contentHash: z.string().regex(/^[a-fA-F0-9]{64}$/, 'Use the original SHA-256 hash').nullable().default(null),
    recordedStatus: z.enum([
        'verifiedCurrent', 'onFileNeedsReview', 'physicalCopyOnFile', 'historicalEvidenceOnFile',
        'currentActionRequired', 'missing', 'notApplicable',
    ]).nullable().default(null),
    url: z.string().url().refine((value) => {
        const url = new URL(value);
        return url.protocol === 'https:' && url.username === '' && url.password === ''
            && ['vigeohealth2.sharepoint.com', 'vigeohealth2-my.sharepoint.com'].includes(url.hostname);
    }, 'Use a Vigeo SharePoint source link').nullable().default(null),
}).strict();
const reason = z.string().trim().min(20, 'Enter a reason of at least 20 characters').max(4000);
const note = z.string().trim().min(1).max(8000);
const sourceEvidence = z.object({
    kind: z.literal('historical-evidence'),
    requirementKey: requirementKeySchema.nullable().default(null),
    source: historicalSourceSchema,
    occurredAt: z.string().datetime(),
    note,
    reason,
}).strict();
const addendum = z.object({
    kind: z.literal('addendum'),
    targetId: z.string().uuid(),
    note,
    reason,
}).strict();
const waiver = z.object({
    kind: z.literal('waiver'),
    requirementKey: requirementKeySchema,
    gate: gateSchema,
    expiresAt: z.string().datetime(),
    reason,
}).strict();
const revoke = z.object({
    kind: z.literal('waiver-revocation'),
    targetId: z.string().uuid(),
    reason,
}).strict();
export const personHistoryInputSchema = z.discriminatedUnion('kind', [sourceEvidence, addendum, waiver, revoke]);
const recorded = {
    id: z.string().uuid(),
    personId: z.string().uuid(),
    docType: z.literal('person-history'),
    recordedAt: z.string().datetime(),
    recordedBy: z.string().min(1),
};
export const personHistoryEntrySchema = z.discriminatedUnion('kind', [
    sourceEvidence.extend(recorded),
    addendum.extend(recorded),
    waiver.extend({ ...recorded, exception: requirementExceptionSchema }),
    revoke.extend(recorded),
]);
export const waiverOptionSchema = z.object({
    requirementKey: requirementKeySchema,
    label: z.string(),
    gate: gateSchema,
    maxDays: z.number().positive().nullable(),
});
export const personHistoryResponseSchema = z.object({
    personId: z.string().uuid(),
    entries: z.array(personHistoryEntrySchema),
    waiverOptions: z.array(waiverOptionSchema),
});
/** Revocation is another record, never a rewrite of the original decision. */
export function historyExceptions(entries) {
    const revoked = new Map();
    for (const entry of [...entries].sort((a, b) => a.recordedAt.localeCompare(b.recordedAt))) {
        if (entry.kind === 'waiver-revocation')
            revoked.set(entry.targetId, entry);
    }
    return entries.flatMap((entry) => {
        if (entry.kind !== 'waiver')
            return [];
        const revocation = revoked.get(entry.id);
        return [{ ...entry.exception,
                ...(revocation ? { revokedAt: revocation.recordedAt, revokedBy: revocation.recordedBy } : {}),
            }];
    });
}
//# sourceMappingURL=history.js.map